import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import {
  ArrowLeft,
  Upload,
  FileText,
  Download,
  Trash2,
  FolderPlus,
  Loader,
  X,
  AlertCircle,
  BookOpen,
  FileSpreadsheet,
  Calculator,
  Eye
} from 'lucide-react';
import { CourseMaterial } from '../types/exam.types';
import { examApi } from '../services/examApi';
import { coursesApi } from '../services/api';
import { analytics } from '../services/analytics';
import { MixedText } from '../utils/rtl';
import { Card, CardHeader, CardTitle, CardContent, SpaceCard } from './ui/Card';
import { Button } from './ui/Button';
import { DashboardContainer } from './layout/PageContainer';
import StudyMaterialRenderer from './StudyMaterialRenderer';

const CourseMaterialsView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const courseIdNum = parseInt(courseId || '0');

  // State
  const [materials, setMaterials] = useState<CourseMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [course, setCourse] = useState<any>(null);
  
  // Upload states
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [materialUploadError, setMaterialUploadError] = useState<string | null>(null);
  
  
  // Delete and preview states
  const [deletingMaterial, setDeletingMaterial] = useState<number | null>(null);
  const [previewMaterial, setPreviewMaterial] = useState<CourseMaterial | null>(null);

  useEffect(() => {
    if (courseIdNum) {
      loadData();
      analytics.track('Course Materials Page Viewed', { course_id: courseIdNum });
    }
  }, [courseIdNum]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load course and materials
      const [courseData, materialsData] = await Promise.all([
        coursesApi.getCourse(courseIdNum),
        examApi.getCourseMaterials(courseIdNum)
      ]);
      
      setCourse(courseData);
      setMaterials(materialsData);
    } catch (err) {
      console.error('Error loading course materials:', err);
      setError(t('common:errors.loading_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialUpload = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    setUploadingMaterial(true);
    setMaterialUploadError(null);

    try {
      const response = await examApi.uploadCourseMaterial(courseIdNum, file, 'reference');
      setMaterials(prev => [...prev, response]);
      
      analytics.track('Course Material Uploaded', {
        course_id: courseIdNum,
        file_name: file.name,
        file_size: file.size,
        material_type: 'reference',
        source: 'materials_page'
      });
    } catch (err) {
      console.error('Error uploading material:', err);
      setMaterialUploadError(t('exams:courseMaterials.uploadError'));
      analytics.trackError('Course Material Upload Failed', {
        courseId: courseIdNum,
        fileName: file.name,
        fileSize: file.size
      });
    } finally {
      setUploadingMaterial(false);
    }
  };

  const handleDeleteMaterial = async (materialId: number, fileName: string) => {
    if (!window.confirm(t('exams:courseMaterials.confirmDelete'))) {
      return;
    }

    setDeletingMaterial(materialId);
    try {
      await examApi.deleteCourseMaterial(materialId);
      setMaterials(prev => prev.filter(m => m.id !== materialId));
      
      analytics.track('Course Material Deleted', {
        course_id: courseIdNum,
        material_id: materialId,
        file_name: fileName,
        source: 'materials_page'
      });
    } catch (err) {
      console.error('Error deleting material:', err);
      setError(t('exams:courseMaterials.deleteError'));
      analytics.trackError('Course Material Delete Failed', {
        courseId: courseIdNum,
        materialId,
        fileName
      });
    } finally {
      setDeletingMaterial(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleMaterialUpload,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    maxFiles: 1,
    disabled: uploadingMaterial
  });

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const getMaterialIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-vibrant-orange" />;
      case 'docx':
      case 'doc':
        return <FileText className="w-5 h-5 text-muted-purple" />;
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
      default:
        return <FileText className="w-5 h-5 text-surface-muted" />;
    }
  };


  if (loading) {
    return (
      <DashboardContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vibrant-orange"></div>
        </div>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <div className="text-center py-20">
          <AlertCircle className="w-12 h-12 text-vibrant-orange mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-surface-primary mb-2">{t('common:errors.something_went_wrong')}</h2>
          <p className="text-surface-secondary mb-6">{error}</p>
          <Button onClick={loadData} variant="primary">
            {t('common:actions.try_again')}
          </Button>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <div className="space-y-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/course/${courseIdNum}/exams`)}
          className={`flex items-center text-bg-secondary hover:text-vibrant-orange transition-colors group font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
        >
          {isRTL ? (
            <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200 ml-3 mr-3" />
          ) : (
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
          )}
          <span className={`font-medium ${isRTL ? 'text-right' : ''}`}>
            <MixedText>{t('common:actions.back_to_exams')}</MixedText>
          </span>
        </button>

        {/* Page Header */}
        <SpaceCard constellation="ursa" className="group hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-4' : 'space-x-4'}`}>
              <div className="w-16 h-16 bg-gradient-to-br from-muted-purple/10 to-muted-purple/20 rounded-2xl flex items-center justify-center">
                <img 
                  src="/astronaut_papers.png" 
                  alt="StudyBel Astronaut with Papers" 
                  className="w-10 h-10 filter brightness-110"
                />
              </div>
              <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                <CardTitle className={`text-2xl md:text-3xl mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('exams:courseMaterials.title', 'Course Materials')}</MixedText>
                </CardTitle>
                <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{course?.name || t('common:labels.course')}</MixedText>
                </p>
              </div>
            </div>
          </CardHeader>
        </SpaceCard>

        {/* Upload Section */}
        <SpaceCard constellation="orion">
          <CardHeader>
            <CardTitle className={`text-xl font-bold mb-4 font-hebrew ${isRTL ? 'text-right' : ''}`}>
              <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Upload className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <MixedText>{t('exams:courseMaterials.upload.title', 'Upload Materials')}</MixedText>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 cursor-pointer ${
                isDragActive
                  ? 'border-muted-purple bg-muted-purple/10 scale-105'
                  : uploadingMaterial
                  ? 'border-surface-muted/30 bg-surface-muted/10 cursor-not-allowed'
                  : 'border-surface-muted/30 hover:border-muted-purple hover:bg-muted-purple/10'
              }`}
            >
              <input {...getInputProps()} />
              {uploadingMaterial ? (
                <div className="space-y-3">
                  <Loader className="w-8 h-8 text-muted-purple mx-auto animate-spin" />
                  <p className="text-muted-purple font-medium text-center">
                    <MixedText>{t('common:messages.uploading')}</MixedText>
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className={`w-8 h-8 mx-auto ${
                    isDragActive ? 'text-muted-purple' : 'text-surface-muted'
                  }`} />
                  <div>
                    <p className="text-surface-primary font-medium text-center">
                      <MixedText>
                        {isDragActive
                          ? t('common:upload.dropFiles')
                          : t('exams:courseMaterials.dragDrop')
                        }
                      </MixedText>
                    </p>
                    <p className="text-sm text-surface-secondary mt-1 text-center">
                      <MixedText>{t('common:upload.supportedFormats')}</MixedText>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {materialUploadError && (
              <div className="mt-4 p-3 bg-vibrant-orange/10 border border-vibrant-orange/30 rounded-lg">
                <p className={`text-vibrant-orange text-sm ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{materialUploadError}</MixedText>
                </p>
              </div>
            )}
          </CardContent>
        </SpaceCard>


        {/* Materials Grid */}
        <SpaceCard constellation="ursa">
          <CardHeader>
            <CardTitle className={`text-xl font-bold font-hebrew ${isRTL ? 'text-right' : ''}`}>
              <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <FolderPlus className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <MixedText>{t('exams:courseMaterials.library.title', 'Materials Library')}</MixedText>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {materials.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-surface-muted/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-surface-muted" />
                </div>
                <h3 className="text-lg font-semibold text-surface-primary mb-2 text-center">
                  <MixedText>{t('exams:courseMaterials.emptyTitle', 'No materials uploaded')}</MixedText>
                </h3>
                <p className="text-surface-secondary text-center">
                  <MixedText>{t('exams:courseMaterials.emptyDescription', 'Upload your first material to get started')}</MixedText>
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {materials.map((material) => (
                  <Card key={material.id} className="group hover:shadow-lg transition-all duration-200">
                    <CardContent className="p-4">
                      <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                        <div className="w-10 h-10 bg-surface-muted/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          {getMaterialIcon(material.original_filename)}
                        </div>
                        <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
                          <h4 className={`font-medium text-surface-primary truncate ${isRTL ? 'text-right' : ''}`}>
                            <MixedText>{material.original_filename}</MixedText>
                          </h4>
                          <p className={`text-sm text-surface-secondary ${isRTL ? 'text-right' : ''}`}>
                            {material.file_size && formatFileSize(material.file_size)} • {t(`exams:examList.materialTypes.${material.material_type}`)}
                          </p>
                        </div>
                      </div>
                      
                      <div className={`flex items-center justify-between mt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                          <button
                            onClick={() => setPreviewMaterial(material)}
                            className="p-2 rounded-lg text-surface-secondary hover:text-muted-purple hover:bg-muted-purple/10 transition-all duration-200"
                            title={t('exams:courseMaterials.preview', 'Preview')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => window.open(material.s3_uri, '_blank')}
                            className="p-2 rounded-lg text-surface-secondary hover:text-vibrant-orange hover:bg-vibrant-orange/10 transition-all duration-200"
                            title={t('exams:courseMaterials.download', 'Download')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => handleDeleteMaterial(material.id, material.original_filename)}
                          disabled={deletingMaterial === material.id}
                          className="p-2 rounded-lg text-surface-secondary hover:text-vibrant-orange hover:bg-vibrant-orange/10 transition-all duration-200"
                          title={t('exams:courseMaterials.delete')}
                        >
                          {deletingMaterial === material.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </SpaceCard>

        {/* Preview Modal */}
        {previewMaterial && (
          <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ background: 'rgba(26, 15, 43, 0.5)' }}>
            <Card className="w-full max-w-4xl shadow-2xl mx-auto max-h-[90vh] overflow-hidden">
              <CardHeader className="border-b border-surface-muted/20">
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CardTitle className={`font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{previewMaterial.original_filename}</MixedText>
                  </CardTitle>
                  <button
                    onClick={() => setPreviewMaterial(null)}
                    className="p-2 hover:bg-surface-muted/10 rounded-xl transition-colors duration-200"
                  >
                    <X className="w-6 h-6 text-surface-secondary" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-6 max-h-[70vh] overflow-y-auto">
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-surface-muted/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    {getMaterialIcon(previewMaterial.original_filename)}
                  </div>
                  <h3 className="text-lg font-semibold text-surface-primary mb-2">
                    <MixedText>{previewMaterial.original_filename}</MixedText>
                  </h3>
                  <p className="text-surface-secondary mb-4">
                    <MixedText>{t('exams:courseMaterials.previewNotAvailable', 'Preview not available for this file type')}</MixedText>
                  </p>
                  <Button
                    onClick={() => window.open(previewMaterial.s3_uri, '_blank')}
                    variant="primary"
                    className="mr-3"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    <MixedText>{t('exams:courseMaterials.download', 'Download')}</MixedText>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardContainer>
  );
};

export default CourseMaterialsView;